import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Video, Loader, AlertCircle, CheckCircle, XCircle,
  MessageSquare, Code, Shield, ChevronDown, ChevronUp, Star,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import {
  viewReport, downloadReportPdf, getRecordingDownloadUrl, getCameraRecordingDownloadUrl,
  downloadRecording, downloadCameraRecording,
} from '../services/api';

function ReportView() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedQA, setExpandedQA] = useState({});
  const [recordingLoading, setRecordingLoading] = useState(false);
  const [cameraRecordingLoading, setCameraRecordingLoading] = useState(false);
  // Availability of recordings: undefined = still checking, null = none, string = present
  const [recordingAvailable, setRecordingAvailable] = useState(undefined);
  const [cameraAvailable, setCameraAvailable] = useState(undefined);

  useEffect(() => {
    fetchReport();
  }, [sessionId]);

  // Probe whether recordings exist so we can show a clear state instead of
  // firing requests that 404 when an interview has no recording.
  useEffect(() => {
    let cancelled = false;
    const probe = async (fn, setter) => {
      try {
        await fn(sessionId);
        if (!cancelled) setter(true);
      } catch {
        if (!cancelled) setter(null); // 404 (or any error) => treat as unavailable
      }
    };
    setRecordingAvailable(undefined);
    setCameraAvailable(undefined);
    probe(getRecordingDownloadUrl, setRecordingAvailable);
    probe(getCameraRecordingDownloadUrl, setCameraAvailable);
    return () => { cancelled = true; };
  }, [sessionId]);

  const fetchReport = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await viewReport(sessionId);
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const toggleQA = (idx) => {
    setExpandedQA((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleDownloadRecording = async () => {
    setRecordingLoading(true);
    try {
      await downloadRecording(sessionId);
    } catch (err) {
      alert(err.response?.status === 404
        ? 'No recording available for this session'
        : 'Failed to download recording');
    } finally {
      setRecordingLoading(false);
    }
  };

  const handleDownloadCameraRecording = async () => {
    setCameraRecordingLoading(true);
    try {
      await downloadCameraRecording(sessionId);
    } catch (err) {
      alert(err.response?.status === 404
        ? 'No camera recording available for this session'
        : 'Failed to download camera recording');
    } finally {
      setCameraRecordingLoading(false);
    }
  };

  const getScoreText = (score, max = 100) => {
    const pct = (score / max) * 100;
    if (pct >= 80) return 'text-green-700';
    if (pct >= 65) return 'text-brand-700';
    if (pct >= 45) return 'text-amber-700';
    return 'text-red-700';
  };

  const getRecStyle = (rec) => {
    if (!rec) return 'bg-ink-800 text-ink-300';
    const r = rec.toLowerCase();
    if (r.includes('strong hire')) return 'bg-green-100 text-green-800 border-green-300';
    if (r.includes('hire')) return 'bg-brand-500/15 text-blue-800 border-blue-300';
    if (r.includes('review')) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  // Labels cover the new interview flow (qa / qa_followup) and older sessions.
  const TYPE_LABELS = {
    qa: 'Interview Questions',
    qa_followup: 'Follow-up Questions',
    ice_breaker: 'Ice Breaker',
    background: 'Background & Projects',
    quick: 'Quick Questions',
    technical: 'Technical Questions',
    soft: 'Soft Skills',
    unknown: 'General Questions',
  };
  const typeLabel = (key) =>
    TYPE_LABELS[key] || String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  const typeColors = {
    qa: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Question' },
    qa_followup: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Follow-up' },
    ice_breaker: { bg: 'bg-ink-700', text: 'text-ink-300', label: 'Ice Breaker' },
    background: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Background' },
    quick: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Quick' },
    technical: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Technical' },
    soft: { bg: 'bg-cyan-100', text: 'text-cyan-700', label: 'Soft Skill' },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-ink-800 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-purple-600 mx-auto mb-3" />
          <p className="text-ink-300">Loading report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-ink-800 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-red-600">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 text-sm text-purple-600 hover:underline">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!report) return null;

  const meta = report.metadata || {};
  const comm = report.communication || {};
  const tech = report.technical || {};
  const proct = report.proctoring || {};
  const code = report.code || {};
  const evaluations = report.all_evaluations || [];

  // Chart data
  const categoryData = Object.entries(report.category_scores || {}).map(([key, val]) => ({
    name: typeLabel(key),
    score: val,
  }));

  const radarData = [
    { subject: 'Communication', value: comm.overall_score || 0, max: 10 },
    { subject: 'Technical Depth', value: tech.knowledge_depth_score || 0, max: 10 },
    { subject: 'Practical', value: tech.practical_application_score || 0, max: 10 },
    { subject: 'Problem Solving', value: tech.problem_solving_score || 0, max: 10 },
    { subject: 'Integrity', value: proct.cheat_score ? Math.max(0, 10 - proct.cheat_score / 10) : 10, max: 10 },
  ];

  return (
    <div className="min-h-screen bg-ink-800">
      {/* Header */}
      <header className="bg-ink-900 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-2 text-ink-400 hover:text-ink-300 rounded-lg hover:bg-ink-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-ink-100">{meta.candidate_name || 'Unknown'}</h1>
              <p className="text-xs text-ink-400">{meta.position} | {meta.organization_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-4 py-1.5 rounded-full text-sm font-bold ${getScoreText(report.overall_score)}`}>
              {Math.round(report.overall_score)}/100
            </span>
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${getRecStyle(report.recommendation)}`}>
              {report.recommendation}
            </span>
            <button
              onClick={handleDownloadRecording}
              disabled={recordingLoading || recordingAvailable !== true}
              title={recordingAvailable === null ? 'No screen recording was captured for this interview' : undefined}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-400 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {recordingLoading || recordingAvailable === undefined
                ? <Loader className="w-4 h-4 animate-spin" />
                : <Video className="w-4 h-4" />}
              {recordingAvailable === null ? 'No Recording' : 'Recording'}
            </button>
            <button
              onClick={handleDownloadCameraRecording}
              disabled={cameraRecordingLoading || cameraAvailable !== true}
              title={cameraAvailable === null ? 'No camera recording was captured for this interview' : undefined}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cameraRecordingLoading || cameraAvailable === undefined
                ? <Loader className="w-4 h-4 animate-spin" />
                : <Video className="w-4 h-4" />}
              {cameraAvailable === null ? 'No Camera' : 'Camera'}
            </button>
            <button
              onClick={() => downloadReportPdf(sessionId)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700"
            >
              <Download className="w-4 h-4" />
              PDF
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">

        {/* Disclaimer */}
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900 mb-1">Report Disclaimer</h3>
              <p className="text-xs text-amber-800 leading-relaxed">
                This report is generated using AI-assisted evaluation and is intended as a supplementary tool for candidate assessment.
                While the system provides objective scoring based on interview responses, final hiring decisions should be made by
                qualified human reviewers who consider the complete context, including factors beyond this automated analysis.
                Please review this report carefully and use it as one of several inputs in your evaluation process.
              </p>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <div className="bg-ink-900 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-bold text-ink-100 mb-3">Executive Summary</h2>
          <p className="text-ink-300 text-sm leading-relaxed">{report.executive_summary}</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <ScoreCard label="Overall" value={report.overall_score} max={100} />
            <ScoreCard label="Communication" value={comm.overall_score || 0} max={10} />
            <ScoreCard label="Technical" value={tech.overall_score || 0} max={10} />
            <ScoreCard
              label="Integrity"
              value={proct.cheat_score != null ? Math.max(0, 100 - proct.cheat_score) : 100}
              max={100}
            />
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <div className="bg-ink-900 rounded-lg shadow-sm p-6">
            <h3 className="text-sm font-bold text-ink-300 mb-4">Category Scores</h3>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={categoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" fontSize={12} />
                <YAxis domain={[0, 10]} fontSize={12} />
                <Tooltip />
                <Bar dataKey="score" fill="#C5A032" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Radar Chart */}
          <div className="bg-ink-900 rounded-lg shadow-sm p-6">
            <h3 className="text-sm font-bold text-ink-300 mb-4">Multi-Dimensional Assessment</h3>
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#e5e7eb" />
                <PolarAngleAxis dataKey="subject" fontSize={10} />
                <PolarRadiusAxis domain={[0, 10]} fontSize={9} />
                <Radar dataKey="value" stroke="#C5A032" fill="#C5A032" fillOpacity={0.3} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Strengths & Weaknesses */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-ink-900 rounded-lg shadow-sm p-6">
            <h3 className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
              <Star className="w-4 h-4" /> Key Strengths
            </h3>
            <ul className="space-y-2">
              {(report.top_strengths || []).map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink-300">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {s}
                </li>
              ))}
              {(!report.top_strengths || report.top_strengths.length === 0) && (
                <li className="text-sm text-ink-500">No strengths identified</li>
              )}
            </ul>
          </div>
          <div className="bg-ink-900 rounded-lg shadow-sm p-6">
            <h3 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Areas for Improvement
            </h3>
            <ul className="space-y-2">
              {(report.top_weaknesses || []).map((w, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink-300">
                  <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  {w}
                </li>
              ))}
              {(!report.top_weaknesses || report.top_weaknesses.length === 0) && (
                <li className="text-sm text-ink-500">No weaknesses identified</li>
              )}
            </ul>
          </div>
        </div>

        {/* Communication Assessment */}
        <div className="bg-ink-900 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-bold text-ink-100 mb-4 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-brand-600" /> Communication Assessment
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <MiniScore label="Clarity" value={comm.clarity_score} />
            <MiniScore label="Language" value={comm.language_quality_score} />
            <MiniScore label="Articulation" value={comm.articulation_score} />
            <MiniScore label="Overall" value={comm.overall_score} />
          </div>
          {comm.summary && <p className="text-sm text-ink-300">{comm.summary}</p>}
          {comm.observations && comm.observations.length > 0 && (
            <div className="mt-3">
              <p className="text-xs font-semibold text-ink-400 mb-1">Observations:</p>
              <ul className="space-y-1">
                {comm.observations.map((o, i) => (
                  <li key={i} className="text-xs text-ink-300 pl-3 border-l-2 border-blue-200">{o}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Technical Assessment */}
        <div className="bg-ink-900 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-bold text-ink-100 mb-4 flex items-center gap-2">
            <Code className="w-5 h-5 text-purple-600" /> Technical Assessment
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <MiniScore label="Depth" value={tech.knowledge_depth_score} />
            <MiniScore label="Practical" value={tech.practical_application_score} />
            <MiniScore label="Problem Solving" value={tech.problem_solving_score} />
            <MiniScore label="Overall" value={tech.overall_score} />
          </div>
          {tech.summary && <p className="text-sm text-ink-300">{tech.summary}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            {tech.strengths && tech.strengths.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-green-600 mb-1">Strengths:</p>
                <ul className="space-y-1">
                  {tech.strengths.map((s, i) => (
                    <li key={i} className="text-xs text-ink-300 pl-3 border-l-2 border-green-200">{s}</li>
                  ))}
                </ul>
              </div>
            )}
            {tech.gaps && tech.gaps.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-red-600 mb-1">Gaps:</p>
                <ul className="space-y-1">
                  {tech.gaps.map((g, i) => (
                    <li key={i} className="text-xs text-ink-300 pl-3 border-l-2 border-red-200">{g}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Proctoring */}
        <div className="bg-ink-900 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-bold text-ink-100 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" /> Proctoring & Integrity
          </h2>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-ink-800 rounded-lg">
              <p className="text-2xl font-bold text-ink-100">{proct.cheat_score || 0}</p>
              <p className="text-xs text-ink-400">Cheat Score</p>
            </div>
            <div className="text-center p-3 bg-ink-800 rounded-lg">
              <p className="text-2xl font-bold text-ink-100">{proct.total_events || 0}</p>
              <p className="text-xs text-ink-400">Total Events</p>
            </div>
            <div className="text-center p-3 bg-ink-800 rounded-lg">
              <p className={`text-lg font-bold ${
                proct.recommendation === 'clear' ? 'text-green-600' :
                proct.recommendation === 'review' ? 'text-amber-600' : 'text-red-600'
              }`}>
                {(proct.recommendation || 'clear').toUpperCase()}
              </p>
              <p className="text-xs text-ink-400">Recommendation</p>
            </div>
          </div>
          {proct.breakdown && Object.keys(proct.breakdown).length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-ink-800">
                    <th className="px-4 py-2 text-left text-xs font-medium text-ink-300">Violation</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-ink-300">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(proct.breakdown).filter(([, v]) => v > 0).map(([key, val]) => (
                    <tr key={key} className="border-t">
                      <td className="px-4 py-2 text-ink-300 capitalize">{key.replace(/_/g, ' ')}</td>
                      <td className="px-4 py-2 text-center font-medium text-ink-100">{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Code Challenge with AI Evaluation */}
        {code.code_submitted && (
          <div className="bg-ink-900 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-bold text-ink-100 mb-4 flex items-center gap-2">
              <Code className="w-5 h-5 text-brand-600" /> Coding Challenge with AI Evaluation
            </h2>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-3 bg-ink-800 rounded-lg">
                <p className="text-lg font-bold text-ink-100">{code.language}</p>
                <p className="text-xs text-ink-400">Language</p>
              </div>
              <div className="text-center p-3 bg-ink-800 rounded-lg">
                <p className="text-lg font-bold text-ink-100">{code.passed_tests}/{code.total_tests}</p>
                <p className="text-xs text-ink-400">Tests Passed</p>
              </div>
              <div className="text-center p-3 bg-ink-800 rounded-lg">
                <p className={`text-lg font-bold ${getScoreText(code.score)}`}>{code.score}%</p>
                <p className="text-xs text-ink-400">Test Score</p>
              </div>
              <div className="text-center p-3 bg-brand-500/10 rounded-lg border-2 border-indigo-200">
                <p className={`text-lg font-bold ${getScoreText(code.partial_score || code.score)}`}>
                  {code.partial_score || code.score}%
                </p>
                <p className="text-xs text-brand-700 font-medium">AI Partial Credit</p>
              </div>
            </div>

            {/* AI Evaluation Dimensions */}
            <div className="mb-6">
              <h3 className="text-sm font-bold text-ink-300 mb-3">AI Code Analysis Breakdown:</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <MiniScore label="Logic/Algorithm" value={code.logic_correctness} />
                <MiniScore label="Code Quality" value={code.code_quality} />
                <MiniScore label="Edge Cases" value={code.edge_case_handling} />
                <MiniScore label="Completeness" value={code.implementation_completeness} />
              </div>
            </div>

            {/* What's Correct */}
            {code.what_is_correct && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-sm font-bold text-green-800 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> What the Candidate Got Right
                </h3>
                <p className="text-sm text-green-900">{code.what_is_correct}</p>
              </div>
            )}

            {/* What's Wrong */}
            {code.what_is_wrong && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4" /> What Needs Improvement
                </h3>
                <p className="text-sm text-red-900">{code.what_is_wrong}</p>
              </div>
            )}

            {/* Expected Approach */}
            {code.expected_approach && (
              <div className="mb-4 p-4 bg-brand-500/10 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-bold text-blue-800 mb-2">Expected Solution Approach:</h3>
                <p className="text-sm text-blue-900">{code.expected_approach}</p>
              </div>
            )}

            {/* Partial Credit Rationale */}
            {code.partial_credit_rationale && (
              <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h3 className="text-sm font-bold text-purple-800 mb-2">Partial Credit Rationale:</h3>
                <p className="text-sm text-purple-900 italic">{code.partial_credit_rationale}</p>
              </div>
            )}

            {/* Suggestions */}
            {code.suggestions && code.suggestions.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-ink-300 mb-2">Suggestions for Improvement:</h3>
                <ul className="space-y-1">
                  {code.suggestions.map((sug, i) => (
                    <li key={i} className="text-sm text-ink-300 pl-3 border-l-2 border-amber-300">• {sug}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Submitted Code */}
            {code.full_code && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-ink-300 mb-2">Submitted Code:</h3>
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg overflow-x-auto text-xs font-mono">
                  {code.full_code}
                </pre>
              </div>
            )}

            {/* Test Results Table */}
            {code.test_results && code.test_results.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-ink-300 mb-2">Test Results:</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-ink-800">
                        <th className="px-3 py-2 text-left font-medium text-ink-300">Test</th>
                        <th className="px-3 py-2 text-left font-medium text-ink-300">Input</th>
                        <th className="px-3 py-2 text-left font-medium text-ink-300">Expected</th>
                        <th className="px-3 py-2 text-left font-medium text-ink-300">Actual</th>
                        <th className="px-3 py-2 text-center font-medium text-ink-300">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {code.test_results.map((tr, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">#{tr.test_number}</td>
                          <td className="px-3 py-2 font-mono truncate max-w-[120px]">{JSON.stringify(tr.input)}</td>
                          <td className="px-3 py-2 font-mono truncate max-w-[120px]">{JSON.stringify(tr.expected)}</td>
                          <td className="px-3 py-2 font-mono truncate max-w-[120px]">{JSON.stringify(tr.actual)}</td>
                          <td className="px-3 py-2 text-center">
                            {tr.passed ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">PASS</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">FAIL</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Q&A Transcript */}
        <div className="bg-ink-900 rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-bold text-ink-100 mb-4">Q&A Transcript ({evaluations.length} questions)</h2>
          <div className="space-y-2">
            {evaluations.map((ev, idx) => {
              const tc = typeColors[ev.question_type] || { bg: 'bg-ink-700', text: 'text-ink-300', label: typeLabel(ev.question_type) };
              const isExpanded = expandedQA[idx];
              return (
                <div key={idx} className="border border-ink-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleQA(idx)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-ink-800 text-left"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${tc.bg} ${tc.text} flex-shrink-0`}>
                        {tc.label}
                      </span>
                      <span className="text-sm text-ink-100 truncate">
                        Q{idx + 1}: {ev.question}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                      <span className={`text-sm font-bold ${getScoreText(ev.final_score * 10)}`}>
                        {ev.final_score}/10
                      </span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-ink-500" /> : <ChevronDown className="w-4 h-4 text-ink-500" />}
                    </div>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-ink-800 bg-ink-800">
                      <div className="mt-3 space-y-3">
                        <div>
                          <p className="text-xs font-semibold text-ink-400 mb-1">Question:</p>
                          <p className="text-sm text-ink-100">{ev.question}</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-ink-400 mb-1">Answer:</p>
                          <p className="text-sm text-ink-300 bg-ink-900 p-3 rounded border border-ink-700">
                            {ev.candidate_response}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-4 text-xs">
                          <span className="text-ink-400">
                            Score: <b>{ev.final_score}/10</b>
                          </span>
                          <span className="text-ink-400">
                            Depth: <b className="capitalize">{ev.depth_assessment}</b>
                          </span>
                        </div>
                        {ev.combined_feedback && (
                          <div>
                            <p className="text-xs font-semibold text-ink-400 mb-1">Feedback:</p>
                            <p className="text-sm text-ink-300 italic">{ev.combined_feedback}</p>
                          </div>
                        )}
                        {ev.key_strengths && ev.key_strengths.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {ev.key_strengths.map((s, i) => (
                              <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">{s}</span>
                            ))}
                          </div>
                        )}
                        {ev.key_weaknesses && ev.key_weaknesses.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {ev.key_weaknesses.map((w, i) => (
                              <span key={i} className="px-2 py-0.5 bg-red-50 text-red-700 rounded text-xs">{w}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Image Question */}
        {report.image_question?.submitted && (
          <div className="bg-ink-900 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-bold text-ink-100 mb-3">Image-Based Question</h2>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-sm font-medium text-ink-300">Status:</span>
              {report.image_question.correct ? (
                <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Correct
                </span>
              ) : (
                <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium flex items-center gap-1">
                  <XCircle className="w-4 h-4" /> Incorrect
                </span>
              )}
            </div>
            {report.image_question.answer && (
              <p className="text-sm text-ink-300"><b>Answer:</b> {report.image_question.answer}</p>
            )}
            {report.image_question.expected_answer && (
              <p className="text-sm text-ink-300"><b>Expected:</b> {report.image_question.expected_answer}</p>
            )}
            {report.image_question.feedback && (
              <p className="text-sm text-ink-400 italic mt-1">{report.image_question.feedback}</p>
            )}
          </div>
        )}

        {/* System Design / Whiteboard Evaluation */}
        {report.system_design?.submitted && (
          <div className="bg-ink-900 rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-bold text-ink-100 mb-4 flex items-center gap-2">
              <Code className="w-5 h-5 text-purple-600" /> System Design / Whiteboard Evaluation
            </h2>

            {/* Question */}
            {report.system_design.question_text && (
              <div className="mb-4 p-3 bg-brand-500/10 border border-blue-200 rounded-lg">
                <p className="text-sm font-bold text-blue-800 mb-1">Task:</p>
                <p className="text-sm text-blue-900">{report.system_design.question_text}</p>
              </div>
            )}

            {/* Whiteboard Image */}
            {report.system_design.canvas_data && (
              <div className="mb-6">
                <h3 className="text-sm font-bold text-ink-300 mb-2">Candidate's Whiteboard Diagram:</h3>
                <div className="border-2 border-ink-700 rounded-lg overflow-hidden bg-ink-900">
                  <img
                    src={report.system_design.canvas_data}
                    alt="Whiteboard diagram"
                    className="w-full max-w-3xl mx-auto"
                  />
                </div>
              </div>
            )}

            {/* Gemini Score */}
            <div className="mb-6">
              <div className="text-center p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg border-2 border-purple-200">
                <p className={`text-3xl font-bold ${getScoreText(report.system_design.gemini_score)}`}>
                  {report.system_design.gemini_score}/100
                </p>
                <p className="text-sm text-purple-700 font-medium">Gemini Vision AI Score</p>
              </div>
            </div>

            {/* Evaluation Dimensions */}
            <div className="mb-6">
              <h3 className="text-sm font-bold text-ink-300 mb-3">AI Architecture Analysis:</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <MiniScore label="Correctness" value={report.system_design.correctness_score} />
                <MiniScore label="Thinking Quality" value={report.system_design.thinking_quality_score} />
                <MiniScore label="Completeness" value={report.system_design.completeness_score} />
                <MiniScore label="Real-World Fit" value={report.system_design.real_world_fit_score} />
                <MiniScore label="Scalability" value={report.system_design.scalability_score} />
                <MiniScore label="Clarity" value={report.system_design.clarity_score} />
              </div>
            </div>

            {/* Components Identified */}
            {report.system_design.components_identified && report.system_design.components_identified.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-ink-300 mb-2">Components Identified:</h3>
                <div className="flex flex-wrap gap-2">
                  {report.system_design.components_identified.map((comp, i) => (
                    <span key={i} className="px-3 py-1 bg-brand-500/10 text-brand-700 rounded-full text-xs font-medium">
                      {comp}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Component Analysis */}
            {report.system_design.component_analysis && (
              <div className="mb-4 p-4 bg-ink-800 border border-ink-700 rounded-lg">
                <h3 className="text-sm font-bold text-ink-100 mb-2">Detailed Component Analysis:</h3>
                <p className="text-sm text-ink-300">{report.system_design.component_analysis}</p>
              </div>
            )}

            {/* Data Flow Assessment */}
            {report.system_design.data_flow_assessment && (
              <div className="mb-4 p-4 bg-brand-500/10 border border-blue-200 rounded-lg">
                <h3 className="text-sm font-bold text-blue-800 mb-2">Data Flow Assessment:</h3>
                <p className="text-sm text-blue-900">{report.system_design.data_flow_assessment}</p>
              </div>
            )}

            {/* Strengths */}
            {report.system_design.strengths && report.system_design.strengths.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-green-700 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Design Strengths
                </h3>
                <ul className="space-y-1">
                  {report.system_design.strengths.map((s, i) => (
                    <li key={i} className="text-sm text-ink-300 pl-3 border-l-2 border-green-300">✓ {s}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Weaknesses */}
            {report.system_design.weaknesses && report.system_design.weaknesses.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                  <XCircle className="w-4 h-4" /> Design Weaknesses
                </h3>
                <ul className="space-y-1">
                  {report.system_design.weaknesses.map((w, i) => (
                    <li key={i} className="text-sm text-ink-300 pl-3 border-l-2 border-red-300">✗ {w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Missing Elements */}
            {report.system_design.missing_elements && report.system_design.missing_elements.length > 0 && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h3 className="text-sm font-bold text-amber-800 mb-2">Missing Critical Elements:</h3>
                <ul className="space-y-1">
                  {report.system_design.missing_elements.map((m, i) => (
                    <li key={i} className="text-sm text-amber-900">− {m}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Trade-offs Considered */}
            {report.system_design.trade_offs_considered && report.system_design.trade_offs_considered.length > 0 && (
              <div className="mb-4">
                <h3 className="text-sm font-bold text-ink-300 mb-2">Trade-offs Considered:</h3>
                <ul className="space-y-1">
                  {report.system_design.trade_offs_considered.map((t, i) => (
                    <li key={i} className="text-sm text-ink-300 pl-3 border-l-2 border-purple-300">⚖ {t}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Scalability Analysis */}
            {report.system_design.scalability_considerations && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-sm font-bold text-green-800 mb-2">Scalability Analysis:</h3>
                <p className="text-sm text-green-900">{report.system_design.scalability_considerations}</p>
              </div>
            )}

            {/* Security Analysis */}
            {report.system_design.security_considerations && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <h3 className="text-sm font-bold text-red-800 mb-2">Security Analysis:</h3>
                <p className="text-sm text-red-900">{report.system_design.security_considerations}</p>
              </div>
            )}

            {/* Overall Assessment */}
            {report.system_design.overall_assessment && (
              <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h3 className="text-sm font-bold text-purple-800 mb-2">Overall Architectural Assessment:</h3>
                <p className="text-sm text-purple-900">{report.system_design.overall_assessment}</p>
              </div>
            )}

            {/* Comprehensive Feedback */}
            {report.system_design.gemini_feedback && (
              <div className="p-4 bg-brand-500/10 border border-indigo-200 rounded-lg">
                <h3 className="text-sm font-bold text-indigo-800 mb-2">Comprehensive Feedback:</h3>
                <p className="text-sm text-indigo-900 italic">{report.system_design.gemini_feedback}</p>
              </div>
            )}
          </div>
        )}

      </main>
    </div>
  );
}

// Helper components
function ScoreCard({ label, value, max }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const color = pct >= 80 ? 'text-green-700' : pct >= 65 ? 'text-brand-700' : pct >= 45 ? 'text-amber-700' : 'text-red-700';
  const bg = pct >= 80 ? 'bg-green-50' : pct >= 65 ? 'bg-brand-500/10' : pct >= 45 ? 'bg-amber-50' : 'bg-red-50';

  return (
    <div className={`${bg} rounded-lg p-4 text-center`}>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-xs text-ink-400 mt-1">{label} (/{max})</p>
    </div>
  );
}

function MiniScore({ label, value }) {
  if (value == null) return null;
  const color = value >= 8 ? 'text-green-700' : value >= 6.5 ? 'text-brand-700' : value >= 4.5 ? 'text-amber-700' : 'text-red-700';
  return (
    <div className="text-center p-2 bg-ink-800 rounded-lg">
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      <p className="text-xs text-ink-400">{label}</p>
    </div>
  );
}

export default ReportView;
